import { ReactNode } from 'react';
import { Form } from 'react-bootstrap';
import {
  AuditCadence,
  BrandInput,
  BusinessModel,
  PrimaryKpi
} from '@blisspointmedia/bpm-types/dist/GoogleAdsAudit';

const BUSINESS_MODEL_OPTIONS: { key: BusinessModel; label: string }[] = [
  { key: 'dtc', label: 'DTC' },
  { key: 'lead_gen', label: 'Lead Gen' },
  { key: 'b2b_saas', label: 'B2B SaaS' },
  { key: 'marketplace', label: 'Marketplace' },
  { key: 'subscription', label: 'Subscription' },
  { key: 'content', label: 'Content' },
  { key: 'other', label: 'Other' }
];

const PRIMARY_KPI_OPTIONS: { key: PrimaryKpi; label: string }[] = [
  { key: 'revenue', label: 'Revenue' },
  { key: 'roas', label: 'ROAS' },
  { key: 'cpa', label: 'CPA' },
  { key: 'leads', label: 'Leads' },
  { key: 'brand', label: 'Brand' }
];

const AUDIT_CADENCE_OPTIONS: { key: AuditCadence; label: string }[] = [
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'ad_hoc', label: 'Ad hoc' }
];

// Narrows a <Form.Select> value against the fixed option list backing it, rather than
// casting the raw string straight into the typed field.
const isOptionKey = <T extends string>(value: string, options: { key: T }[]): value is T =>
  options.some(o => o.key === value);

interface BrandProfileFieldsProps {
  form: BrandInput;
  setForm: (updater: (prev: BrandInput) => BrandInput) => void;
  marketsText: string;
  setMarketsText: (value: string) => void;
  contactsText: string;
  setContactsText: (value: string) => void;
}

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className='google-ads-audit-form-section'>
    <div className='google-ads-audit-form-section-title'>{title}</div>
    {children}
  </div>
);

const Row = ({ children }: { children: ReactNode }) => (
  <div className='google-ads-audit-form-row'>{children}</div>
);

export const BrandProfileFields = ({
  form,
  setForm,
  marketsText,
  setMarketsText,
  contactsText,
  setContactsText
}: BrandProfileFieldsProps) => (
  <div className='google-ads-audit-form'>
    <Section title='Audit context'>
      <Form.Group>
        <Form.Label>What should the audit focus on?</Form.Label>
        <Form.Control
          as='textarea'
          rows={3}
          value={form.auditContext ?? ''}
          onChange={e => setForm(prev => ({ ...prev, auditContext: e.target.value }))}
        />
      </Form.Group>
    </Section>

    <Section title='Business profile'>
      <Row>
        <Form.Group>
          <Form.Label>Industry</Form.Label>
          <Form.Control
            value={form.industry ?? ''}
            onChange={e => setForm(prev => ({ ...prev, industry: e.target.value }))}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Business model</Form.Label>
          <Form.Select
            value={form.businessModel ?? ''}
            onChange={e => {
              const { value } = e.target;
              if (value === '') {
                setForm(prev => ({ ...prev, businessModel: null }));
              } else if (isOptionKey(value, BUSINESS_MODEL_OPTIONS)) {
                setForm(prev => ({ ...prev, businessModel: value }));
              }
            }}
          >
            <option value=''>—</option>
            {BUSINESS_MODEL_OPTIONS.map(o => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Row>
      <Row>
        <Form.Group>
          <Form.Label>Primary KPI</Form.Label>
          <Form.Select
            value={form.primaryKpi ?? ''}
            onChange={e => {
              const { value } = e.target;
              if (value === '') {
                setForm(prev => ({ ...prev, primaryKpi: null }));
              } else if (isOptionKey(value, PRIMARY_KPI_OPTIONS)) {
                setForm(prev => ({ ...prev, primaryKpi: value }));
              }
            }}
          >
            <option value=''>—</option>
            {PRIMARY_KPI_OPTIONS.map(o => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <Form.Group>
          <Form.Label>Avg. order value</Form.Label>
          <Form.Control
            type='number'
            value={form.avgOrderValue ?? ''}
            onChange={e =>
              setForm(prev => ({
                ...prev,
                avgOrderValue: e.target.value === '' ? null : Number(e.target.value)
              }))
            }
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Avg. lead value</Form.Label>
          <Form.Control
            type='number'
            value={form.avgLeadValue ?? ''}
            onChange={e =>
              setForm(prev => ({
                ...prev,
                avgLeadValue: e.target.value === '' ? null : Number(e.target.value)
              }))
            }
          />
        </Form.Group>
      </Row>
      <Form.Group>
        <Form.Label>Markets (comma-separated)</Form.Label>
        <Form.Control value={marketsText} onChange={e => setMarketsText(e.target.value)} />
      </Form.Group>
      <Form.Group>
        <Form.Label>Seasonality notes</Form.Label>
        <Form.Control
          as='textarea'
          rows={2}
          value={form.seasonalityNotes ?? ''}
          onChange={e => setForm(prev => ({ ...prev, seasonalityNotes: e.target.value }))}
        />
      </Form.Group>
    </Section>

    <Section title='Account team'>
      <Row>
        <Form.Group>
          <Form.Label>Tinuiti account manager</Form.Label>
          <Form.Control
            value={form.tinuitiAccountManager ?? ''}
            onChange={e => setForm(prev => ({ ...prev, tinuitiAccountManager: e.target.value }))}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Service tier</Form.Label>
          <Form.Control
            value={form.serviceTier ?? ''}
            onChange={e => setForm(prev => ({ ...prev, serviceTier: e.target.value }))}
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Audit cadence</Form.Label>
          <Form.Select
            value={form.auditCadence ?? ''}
            onChange={e => {
              const { value } = e.target;
              if (value === '') {
                setForm(prev => ({ ...prev, auditCadence: null }));
              } else if (isOptionKey(value, AUDIT_CADENCE_OPTIONS)) {
                setForm(prev => ({ ...prev, auditCadence: value }));
              }
            }}
          >
            <option value=''>—</option>
            {AUDIT_CADENCE_OPTIONS.map(o => (
              <option key={o.key} value={o.key}>
                {o.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Row>
      <Form.Group>
        <Form.Label>Client contacts (JSON: name, email, role)</Form.Label>
        <Form.Control
          as='textarea'
          rows={3}
          value={contactsText}
          onChange={e => setContactsText(e.target.value)}
        />
      </Form.Group>
    </Section>

    <Section title='Brand guidance'>
      <Form.Group>
        <Form.Label>Brand guidelines</Form.Label>
        <Form.Control
          as='textarea'
          rows={2}
          value={form.brandGuidelines ?? ''}
          onChange={e => setForm(prev => ({ ...prev, brandGuidelines: e.target.value }))}
        />
      </Form.Group>
      <Form.Group>
        <Form.Label>Promotional calendar</Form.Label>
        <Form.Control
          as='textarea'
          rows={2}
          value={form.promotionalCalendar ?? ''}
          onChange={e => setForm(prev => ({ ...prev, promotionalCalendar: e.target.value }))}
        />
      </Form.Group>
    </Section>
  </div>
);
